import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Truck, Search, AlertCircle, CheckCircle, XCircle, Lock, Mail } from 'lucide-react';
import useSWR, { useSWRConfig } from 'swr';
import { useAuthStore } from '../hooks/useAuth';
import { useUIStore } from '../hooks/useUIStore';
import { useAutoRefreshInterval } from '../hooks/useAutoRefreshInterval';
import { TableSkeleton } from '../components/ui/TableSkeleton';
import { CarrierDrawer } from '../components/carriers/CarrierDrawer';
import { isOperationsTierUser } from '../components/entitlements/OperationsFeatureGate';

interface Carrier {
    id: string;
    name: string;
    email: string;
    phone?: string;
    mcNumber?: string;
    dotNumber?: string;
    status: string;
    rating?: number | string | null;
    confidenceLabel?: string;
    createdAt: string | null;
    onTimePct?: number;
    loadsCompleted?: number;
    avgDwellMinutes?: number;
    lastActivityAt?: string | null;
    // Live 30d metrics from backend aggregate
    appointmentCount30d?: number;
    appointmentCountAllTime?: number;
    lastSeenAt?: string | null;
    avgDwellMinutes30d?: number | null;
    onTimePct30d?: number | null;
}

const CONFIDENCE_BADGE_COLORS: Record<string, string> = {
    'High': 'text-emerald-600 dark:text-emerald-500',
    'Medium': 'text-amber-600 dark:text-amber-500',
    'Low': 'text-slate-500 dark:text-slate-400',
    'Not established': 'text-slate-500 dark:text-slate-400',
};

export function Carriers() {
    const { token, user } = useAuthStore();
    const isOperationsEntitled = isOperationsTierUser(user);
    const { mutate } = useSWRConfig();
    const { registerActionSuccessListener, unregisterActionSuccessListener } = useUIStore();
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState<keyof Carrier>(isOperationsEntitled ? 'lastSeenAt' : 'createdAt');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);
    const highlightHandled = useRef(false);

    // Stable refresh fn — required so unregisterActionSuccessListener removes the same ref
    const refreshCarriers = useCallback(() => mutate('/api/carriers'), [mutate]);

    // Register instant-refresh callback: fired after any approve action
    useEffect(() => {
        registerActionSuccessListener(refreshCarriers);
        return () => unregisterActionSuccessListener(refreshCarriers);
    }, [refreshCarriers, registerActionSuccessListener, unregisterActionSuccessListener]);

    // Auto-refresh subscription
    const refreshInterval = useAutoRefreshInterval();

    // Fetch data
    const { data, error, isLoading } = useSWR<{ carriers: Carrier[]; scorecardEntitled?: boolean }>(
        token ? '/api/carriers' : null,
        async (url: string) => {
            const headers: Record<string, string> = {
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'Accept': 'application/json'
            };

            const empty = () => ({ carriers: [] as Carrier[] });

            const safeParse = (text: string) => {
                try {
                    return text ? JSON.parse(text) : null;
                } catch (e) {
                    console.warn('[Carriers] Failed to parse JSON:', text?.slice(0, 200));
                    return null;
                }
            };

            const normalizeData = (json: any) => {
                if (Array.isArray(json)) return { carriers: json };
                if (json && Array.isArray(json.carriers)) return { carriers: json.carriers, scorecardEntitled: json.scorecardEntitled };
                if (json && json.carriers && Array.isArray(json.carriers.carriers)) return { carriers: json.carriers.carriers };
                if (json && Array.isArray(json.data)) return { carriers: json.data };
                if (json && Array.isArray(json.items)) return { carriers: json.items };
                if (json && typeof json === 'object') {
                    console.warn('[Carriers] Unexpected response keys:', Object.keys(json));
                }
                return empty();
            };

            try {
                let res = await fetch(url, { cache: 'no-store', headers });

                if (res.status === 204) return empty();

                if (res.status === 304) {
                    const bustedUrl = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
                    res = await fetch(bustedUrl, { cache: 'no-store', headers });
                    if (res.status === 304) return empty();
                }

                if (!res.ok) {
                    const body = await res.text().catch(() => '');
                    console.warn('[Carriers] Non-OK response', {
                        status: res.status,
                        statusText: res.statusText,
                        contentType: res.headers.get('content-type'),
                        body: body.slice(0, 200)
                    });
                    return empty();
                }

                const text = await res.text();
                if (!text?.trim()) return empty();

                const json = safeParse(text);
                return normalizeData(json);
            } catch (e) {
                console.warn('[Carriers] Fetcher exception (returning empty)', e);
                return empty();
            }
        },
        {
            refreshInterval
        }
    );

    const carriers = data?.carriers || [];

    // Auto-open drawer when navigated with ?highlight=<carrierKey>
    useEffect(() => {
        if (highlightHandled.current || carriers.length === 0) return;
        const params = new URLSearchParams(window.location.search);
        const highlightRaw = params.get('highlight');
        if (!highlightRaw) return;

        // Mark handled immediately so this doesn't re-run
        highlightHandled.current = true;

        const highlight = highlightRaw.trim();
        const normMc = (v?: string | null) => (v || '').replace(/[^0-9]/g, '');
        const highlightMc = normMc(highlight);

        const match = carriers.find(c =>
            c.id === highlight ||
            (highlightMc && normMc(c.mcNumber) === highlightMc)
        );

        if (match) {
            setSelectedCarrier(match);
        }
    }, [carriers]);

    // Filter & Sort
    const filteredCarriers = useMemo(() => {
        return carriers
            .filter(c =>
                (c.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (c.mcNumber ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (c.email ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (c.phone ?? '').toLowerCase().includes(searchQuery.toLowerCase())
            )
            .sort((a, b) => {
                const valA = a[sortField];
                const valB = b[sortField];

                if (valA === undefined || valB === undefined) return 0;

                let compare = 0;
                // Date sorting
                if (sortField === 'createdAt' || sortField === 'lastActivityAt') {
                    const aStr = typeof valA === 'string' ? valA : null;
                    const bStr = typeof valB === 'string' ? valB : null;

                    const timeA = aStr ? new Date(aStr).getTime() : NaN;
                    const timeB = bStr ? new Date(bStr).getTime() : NaN;

                    // push null/invalid to bottom (both directions)
                    const aBad = Number.isNaN(timeA);
                    const bBad = Number.isNaN(timeB);
                    if (aBad && bBad) return 0;
                    if (aBad) return 1;
                    if (bBad) return -1;

                    compare = timeA - timeB;
                }
                // String sorting (case-insensitive)
                else if (typeof valA === 'string' && typeof valB === 'string') {
                    compare = valA.localeCompare(valB, undefined, { sensitivity: 'base' });
                }
                // Number fallback
                else {
                    const numA = Number(valA);
                    const numB = Number(valB);
                    compare = numA < numB ? -1 : numA > numB ? 1 : 0;
                }

                return sortDirection === 'asc' ? compare : -compare;
            });
    }, [carriers, searchQuery, sortField, sortDirection]);

    const handleSort = (field: keyof Carrier) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
            if (field === 'onTimePct' || field === 'avgDwellMinutes' || field === 'rating') {
                setSortDirection('desc');
            }
        }
    };

    if (isLoading) return <TableSkeleton />;

    if (error) {
        return (
            <div className="p-8 text-center">
                <div className="inline-flex p-4 rounded-full bg-red-500/10 text-red-400 mb-4">
                    <AlertCircle size={32} />
                </div>
                <h2 className="text-xl font-bold mb-2">Failed to load carriers</h2>
                <p className="text-muted-foreground">Unable to connect to the carrier service.</p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1600px] mx-auto flex flex-col min-h-0 h-full">
            {/* Header */}
            <div className="flex justify-between items-center mb-6 shrink-0 pl-12 md:pl-0">
                <div>
                    <h1 className="text-3xl font-bold font-outfit tracking-tight flex items-center gap-3">
                        <Truck className="text-primary" /> Carriers
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {isOperationsEntitled
                            ? 'Manage carrier network, compliance, and 30-day performance scorecards'
                            : 'Manage carrier contact directory and MC / DOT compliance'}
                    </p>
                </div>
                <div className="flex gap-2">
                    {/* Add Carrier Button could go here */}
                </div>
            </div>

            {/* Starter notice for Carrier Performance Scorecards */}
            {!isOperationsEntitled && (
                <div
                    data-testid="carriers-scorecard-upgrade-banner"
                    className="mb-6 rounded-xl border border-cyan-500/30 bg-cyan-500/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0"
                >
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-lg bg-cyan-500/10 p-2 text-cyan-600 dark:text-cyan-400 shrink-0">
                            <Lock className="w-4 h-4" />
                        </div>
                        <div className="text-xs text-muted-foreground leading-relaxed">
                            <span className="font-semibold text-foreground">Carrier Performance Scorecards require Operations ($199 USD/month per facility).</span>{' '}
                            Your Starter plan ($99 USD/month) includes the full Carrier Contact Directory, MC/DOT compliance lookup, and carrier autocomplete during booking. Upgrade to Operations for weighted reliability ratings, 30-day on-time %, and average dwell scorecards.
                        </div>
                    </div>
                    <a
                        href="mailto:support@baywhiz.com?subject=BayWhiz%20Operations%20Plan%20Upgrade%20-%20Carrier%20Scorecards"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold transition-colors shrink-0 self-start sm:self-center"
                    >
                        <Mail className="w-3.5 h-3.5" />
                        support@baywhiz.com
                    </a>
                </div>
            )}

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6 shrink-0">
                <div className="relative flex-1 max-w-full sm:max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search carriers by name, MC#, or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg focus:ring-1 focus:ring-primary focus:outline-none transition-all text-foreground"
                    />
                </div>
            </div>

            {/* Content Table */}
            <div className="flex-1 overflow-auto rounded-xl border border-border bg-card/50 backdrop-blur-sm relative shadow-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-muted/50 sticky top-0 z-10 backdrop-blur-md uppercase tracking-wider">
                        <tr>
                            <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors border-r border-border/20 last:border-r-0"
                                onClick={() => handleSort('name')}>
                                Company Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th className="p-4 font-semibold text-sm text-muted-foreground border-r border-border/20 last:border-r-0">MC / DOT</th>
                            <th className="p-4 font-semibold text-sm text-muted-foreground border-r border-border/20 last:border-r-0">Phone</th>
                            <th className="p-4 font-semibold text-sm text-muted-foreground border-r border-border/20 last:border-r-0">Email</th>
                            {isOperationsEntitled && (
                                <>
                                    <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground text-right border-r border-border/20 last:border-r-0"
                                        onClick={() => handleSort('onTimePct30d')}>
                                        On-time (30d) {sortField === 'onTimePct30d' && (sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground text-right border-r border-border/20 last:border-r-0"
                                        onClick={() => handleSort('avgDwellMinutes30d')}>
                                        Dwell (30d) {sortField === 'avgDwellMinutes30d' && (sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground text-right border-r border-border/20 last:border-r-0"
                                        onClick={() => handleSort('appointmentCount30d')}>
                                        Loads (30d) {sortField === 'appointmentCount30d' && (sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground border-r border-border/20 last:border-r-0"
                                        onClick={() => handleSort('lastSeenAt')}>
                                        Last Seen {sortField === 'lastSeenAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                </>
                            )}
                            <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground border-r border-border/20 last:border-r-0"
                                onClick={() => handleSort('status')}>
                                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground border-r border-border/20 last:border-r-0"
                                onClick={() => handleSort('createdAt')}>
                                Created {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {filteredCarriers.length === 0 ? (
                            <tr>
                                <td colSpan={isOperationsEntitled ? 10 : 6} className="p-8 text-center text-muted-foreground">
                                    {searchQuery.trim()
                                        ? `No carriers found matching "${searchQuery}"`
                                        : "No carriers found."}
                                </td>
                            </tr>
                        ) : (
                            filteredCarriers.map((carrier) => (
                                <tr
                                    key={carrier.id}
                                    onClick={() => {
                                        if (!window.getSelection()?.toString()) {
                                            setSelectedCarrier(carrier);
                                        }
                                    }}
                                    className="hover:bg-muted/30 transition-colors group cursor-pointer"
                                >
                                    <td className="p-4 border-r border-border/20 last:border-r-0">
                                        <div className="font-medium text-foreground">{carrier.name}</div>
                                        {isOperationsEntitled && (carrier.rating !== undefined && carrier.rating !== null && !isNaN(Number(carrier.rating))) && (
                                            <div className="flex items-center gap-1.5 text-xs">
                                                <span className="text-amber-500 dark:text-amber-400">★ {Number(carrier.rating).toFixed(1)}</span>
                                                {carrier.confidenceLabel && (
                                                    <span className={`${CONFIDENCE_BADGE_COLORS[carrier.confidenceLabel] || 'text-slate-400'}`}>
                                                        · {carrier.confidenceLabel}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 border-r border-border/20 last:border-r-0">
                                        <div className="flex flex-col text-sm">
                                            <span className="font-mono text-xs text-muted-foreground">MC: {carrier.mcNumber || '-'}</span>
                                            <span className="font-mono text-xs text-muted-foreground">DOT: {carrier.dotNumber || '-'}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-sm text-muted-foreground border-r border-border/20 last:border-r-0">{carrier.phone || '—'}</td>
                                    <td className="p-4 text-sm text-muted-foreground break-all border-r border-border/20 last:border-r-0">{carrier.email || '—'}</td>

                                    {isOperationsEntitled && (
                                        <>
                                            {/* On-time 30d */}
                                            <td className="p-4 text-right tabular-nums border-r border-border/20 last:border-r-0">
                                                <span className={`font-mono text-sm ${carrier.onTimePct30d == null ? 'text-muted-foreground' :
                                                    carrier.onTimePct30d >= 90 ? 'text-emerald-500' :
                                                        carrier.onTimePct30d >= 70 ? 'text-amber-500' : 'text-red-500'
                                                    }`}>
                                                    {carrier.onTimePct30d != null ? `${carrier.onTimePct30d}%` : '—'}
                                                </span>
                                            </td>
                                            {/* Avg Dwell 30d */}
                                            <td className="p-4 text-right tabular-nums border-r border-border/20 last:border-r-0">
                                                <span className="font-mono text-sm text-muted-foreground">
                                                    {carrier.avgDwellMinutes30d != null ? `${carrier.avgDwellMinutes30d}m` : '—'}
                                                </span>
                                            </td>
                                            {/* Loads 30d */}
                                            <td className="p-4 font-mono text-sm text-muted-foreground text-right tabular-nums border-r border-border/20 last:border-r-0">
                                                {carrier.appointmentCount30d ?? 0}
                                            </td>
                                            {/* Last Seen */}
                                            <td className="p-4 text-sm text-muted-foreground font-mono border-r border-border/20 last:border-r-0">
                                                {(() => {
                                                    const ts = carrier.lastSeenAt || carrier.lastActivityAt;
                                                    if (!ts) return '—';
                                                    const d = new Date(ts);
                                                    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
                                                })()}
                                            </td>
                                        </>
                                    )}
                                    {/* Status */}
                                    <td className="p-4 border-r border-border/20 last:border-r-0">
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${carrier.status === 'active'
                                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                                            : 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30'
                                            }`}>
                                            {carrier.status === 'active' ? <CheckCircle size={10} /> : <XCircle size={10} />}
                                            {carrier.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="p-4 text-sm text-muted-foreground font-mono border-r border-border/20 last:border-r-0">
                                        {(() => {
                                            if (!carrier.createdAt) return '—';
                                            const d = new Date(carrier.createdAt);
                                            return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
                                        })()}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <CarrierDrawer
                carrier={selectedCarrier}
                open={!!selectedCarrier}
                onClose={() => setSelectedCarrier(null)}
            />
        </div>
    );
}
